import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './Pages.css';
import sollaiSol from '../assets/sollaiSol.png';
import familyImage from '../assets/family.png';

function HomePage() {
  const [currentTagline, setCurrentTagline] = useState(0);
  const [isVisible, setIsVisible] = useState({});

  const taglines = [
    "Learn Together",
    "Play Together",
    "Grow Together",
    "Connect Across Generations"
  ];

  // Trigger good ads
  useEffect (() => {
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch (e) {
      console.error('Adsense error:', e);
    }
  }, []);

  // Rotating taglines
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTagline((prev) => (prev + 1) % taglines.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({
              ...prev,
              [entry.target.id]: true
            }));
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('.animate-on-scroll');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const benefits = [
    {
      icon: '🎮',
      title: 'Bilingual Gaming',
      description: 'Seamlessly switch between Tamil and English, making language learning fun and natural for all ages.'
    },
    {
      icon: '👨‍👩‍👧‍👦',
      title: 'Family Bonding',
      description: 'Games designed to bring grandparents, parents, and children together for memorable moments.'
    },
    {
      icon: '🌍',
      title: 'Cultural Connection',
      description: 'Stay connected to Tamil heritage no matter where in the world your family is.'
    },
    {
      icon: '🧠',
      title: 'Learning Through Play',
      description: 'Develop vocabulary, quick thinking, and communication skills while having fun.'
    }
  ];

  const stats = [
    { number: '10K+', label: 'Active Players' },
    { number: '5K+', label: 'Families Connected' },
    { number: '100K+', label: 'Games Played' },
    { number: '50+', label: 'Countries' }
  ];

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-overlay"></div>
        <div className="hero-wrapper">
          <div className="hero-content">
            <h1 className="hero-title">
              Connecting Generations Through Tamil Games
            </h1>
            <p className="hero-tagline">
              {taglines[currentTagline]}
            </p>
            <p className="hero-subtitle">
              Where tradition meets technology, and families come together to play, laugh, and learn. 
              Perfect for bringing everyone together this Christmas! 🎄
            </p>
            <div className="hero-buttons">
              <Link to="/ourapps" className="btn btn-primary">
                Explore Our Games
              </Link>
              <Link to="/about" className="btn btn-secondary">
                Our Story
              </Link>
            </div>
          </div>
          <div className="hero-image-container">
            <img 
              src={familyImage}
              alt="Family playing games together"
              className="hero-image"
            />
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section 
        id="benefits" 
        className={`benefits-section animate-on-scroll ${isVisible.benefits ? 'visible' : ''}`}
      >
        <div className="section-header">
          <h2>Why Families Love Us</h2>
          <p>More than just games – building bridges across generations and borders</p>
        </div>
        <div className="benefits-grid">
          {benefits.map((benefit, index) => (
            <div 
              key={index} 
              className="benefit-card"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="benefit-icon">{benefit.icon}</div>
              <h3>{benefit.title}</h3>
              <p>{benefit.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Featured Game Section */}
      <section 
        id="featured-game" 
        className={`featured-game-section animate-on-scroll ${isVisible['featured-game'] ? 'visible' : ''}`}
      >
        <div className="featured-game-content">
          <div className="featured-game-text">
            <span className="featured-badge">Featured Game</span>
            <h2>Sollai Sol</h2>
            <p className="featured-tagline">The Ultimate Bilingual Party Game!</p>
            <p className="featured-description">
              Race against time as you describe cards to your team while they guess as fast as they can. 
              Perfect for bringing families and friends together across generations with laughter, quick 
              thinking, and unforgettable moments. The perfect addition to your Christmas celebrations!
            </p>
            <div className="featured-highlights">
              <div className="highlight-item">
                <span className="highlight-icon">⚡</span>
                <span>Fast-paced action</span>
              </div>
              <div className="highlight-item">
                <span className="highlight-icon">🎯</span>
                <span>Easy to learn</span>
              </div>
              <div className="highlight-item">
                <span className="highlight-icon">👥</span>
                <span>4+ players</span>
              </div>
            </div>
            <Link to="/ourapps" className="btn btn-primary">
              Download Now
            </Link>
          </div>
          <div className="featured-game-image">
            <a href='/ourapps'><img src={sollaiSol} alt="Sollai Sol Game" /></a>
          </div>
        </div>
      </section>

      {/* Statistics Section - COMMENTED OUT FOR LATER USE
      <section 
        id="stats" 
        className={`stats-section animate-on-scroll ${isVisible.stats ? 'visible' : ''}`}
      >
        <div className="stats-grid">
          {stats.map((stat, index) => (
            <div 
              key={index} 
              className="stat-card"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="stat-number">{stat.number}</div>
              <div className="stat-label">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>
      */}

      {/* How It Works Section */}
      <section 
        id="how-it-works" 
        className={`how-it-works-section animate-on-scroll ${isVisible['how-it-works'] ? 'visible' : ''}`}
      >
        <div className="section-header">
          <h2>Getting Started is Easy</h2>
        </div>
        <div className="steps-container">
          <div className="step-card">
            <div className="step-number">1</div>
            <div className="step-icon">📱</div>
            <h3>Download</h3>
            <p>Get our free games from Play Store or App Store</p>
          </div>
          <div className="step-arrow">→</div>
          <div className="step-card">
            <div className="step-number">2</div>
            <div className="step-icon">👨‍👩‍👧‍👦</div>
            <h3>Gather</h3>
            <p>Bring your family together – in person or virtually</p>
          </div>
          <div className="step-arrow">→</div>
          <div className="step-card">
            <div className="step-number">3</div>
            <div className="step-icon">🎉</div>
            <h3>Play & Connect</h3>
            <p>Create memories while learning and laughing together</p>
          </div>
        </div>
      </section>

      <section className="cta-section">
        <div className="cta-content">
          <h2>Ready to Bring Your Family Together?</h2>
          <p>
            Join thousands of families worldwide who are creating lasting memories while 
            celebrating Tamil culture. Download our games today and start your journey!
          </p>
          <div className="cta-buttons">
            <Link to="/ourapps" className="btn btn-primary btn-large">
              Get Started Free
            </Link>
            <Link to="/contact" className="btn btn-secondary-outline btn-large">
              Get in Touch
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default HomePage;
